import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { format as formatSql } from 'sql-formatter';
import { SqlEditor, type SqlEditorHandle } from '@/components/editor/SqlEditor';
import type { CatalogSnapshot } from '@/components/editor/catalogCompletion';
import { ResultGrid } from '@/components/grid/ResultGrid';
import { ModeStrip } from '@/components/grid/ModeStrip';
import { ExportMenu } from '@/components/toolbar/ExportMenu';
import { EmptyState, ErrorState, Spinner } from '@/components/common/states';
import { formatBytes, shortcut } from '@/lib/format';
import { download, suggestedFilename, toCsv, toJson, type ExportFormat } from '@/lib/export';
import { fetchExport, requestExport } from '@/api/exports';
import type { LocalQueryResult } from '@/cache/types';
import type { CellResult } from '@/query/resultStore';
import { CellStatusBar } from './CellStatusBar';
import { FilterSortPanel, type AppliedFilterSort } from '@/components/filter/FilterSortPanel';
import {
  describeFilterSort,
  EMPTY_FILTER_SORT,
  newFilterRow,
  type FilterSortState,
} from '@/components/filter/filterModel';
import type { DistinctValue } from '@/cache/ResultCache';
import { GroupPanel } from '@/components/group/GroupPanel';
import { describeGroup, EMPTY_GROUP, isGroupActive, type GroupState } from '@/components/group/groupModel';

export interface NotebookCellProps {
  readonly cellId: string;
  readonly index: number;
  readonly total: number;
  readonly sql: string;
  readonly result: CellResult;
  readonly active: boolean;
  readonly expanded: boolean;
  readonly getCatalog: () => CatalogSnapshot;
  readonly onSqlChange: (sql: string) => void;
  readonly onFocus: () => void;
  readonly onRun: () => void;
  readonly onRunSelection: (selection: string) => void;
  readonly onRunAndAdvance: () => void;
  readonly onCancel: () => void;
  readonly onDelete: () => void;
  readonly onDuplicate: () => void;
  readonly onMove: (direction: -1 | 1) => void;
  readonly onAddBelow: () => void;
  readonly onSortChange: (sort: CellResult['sort']) => void;
  readonly onFiltersChange: (filters: CellResult['filters']) => void;
  readonly onColumnOrderChange: (order: readonly string[]) => void;
  readonly onToggleColumn: (name: string) => void;
  readonly onColumnWidthsChange: (widths: Readonly<Record<string, number>>) => void;
  readonly onFocusColumn: (name: string) => void;
  readonly onExportParquet: () => Promise<Uint8Array | null>;
  readonly onExportData: () => Promise<LocalQueryResult | null>;
  readonly filterSort: FilterSortState;
  readonly onFilterSortChange: (state: FilterSortState) => void;
  readonly onApplyFilterSort: (applied: AppliedFilterSort) => void;
  readonly onLoadDistinct: (column: string) => Promise<readonly DistinctValue[] | null>;
  readonly group: GroupState;
  readonly onGroupChange: (state: GroupState) => void;
  readonly onApplyGroup: (state: GroupState | null) => void;
}

export interface NotebookCellHandle {
  focusEditor: () => void;
  insertAtCursor: (text: string) => void;
  getSelection: () => string;
}

export const NotebookCell = forwardRef<NotebookCellHandle, NotebookCellProps>(
  function NotebookCell(props, ref) {
    const {
      cellId,
      index,
      total,
      sql,
      result,
      active,
      expanded,
      getCatalog,
      onSqlChange,
      onFocus,
      onRun,
      onRunSelection,
      onRunAndAdvance,
      onCancel,
      onDelete,
      onDuplicate,
      onMove,
      onAddBelow,
      onSortChange,
      onFiltersChange,
      onColumnOrderChange,
      onToggleColumn,
      onColumnWidthsChange,
      onFocusColumn,
      onExportParquet,
      onExportData,
      filterSort,
      onFilterSortChange,
      onApplyFilterSort,
      onLoadDistinct,
      group,
      onGroupChange,
      onApplyGroup,
    } = props;

    const editorRef = useRef<SqlEditorHandle>(null);
    const [formatError, setFormatError] = useState<string | null>(null);
    const [filterOpen, setFilterOpen] = useState(false);
    const [groupOpen, setGroupOpen] = useState(false);

    useImperativeHandle(ref, () => ({
      focusEditor: () => editorRef.current?.focus(),
      insertAtCursor: (text: string) => editorRef.current?.insertAtCursor(text),
      getSelection: () => editorRef.current?.getSelection() ?? '',
    }));

    const formatCurrent = useCallback(() => {
      try {
        const pretty = formatSql(editorRef.current?.getValue() ?? sql, {
          language: 'duckdb',
          keywordCase: 'upper',
          tabWidth: 2,
        });
        editorRef.current?.setValue(pretty);
        onSqlChange(pretty);
        setFormatError(null);
      } catch (cause) {
        setFormatError(cause instanceof Error ? cause.message : 'Could not format this SQL.');
        window.setTimeout(() => setFormatError(null), 2600);
      }
    }, [sql, onSqlChange]);

    /**
     * Export policy, in one place: cached results export from the browser
     * (faster, no round trip); everything else goes to the server, which
     * re-runs the query under COPY and can cover far more rows than the grid
     * ever held.
     */
    const runExport = useCallback(
      async (format: ExportFormat, report: (message: string) => void): Promise<void> => {
        if (result.mode === 'local') {
          report('Reading cached rows…');
          if (format === 'parquet') {
            const bytes = await onExportParquet();
            if (bytes) {
              // Copy out of the WASM heap: that buffer can be detached before
              // the Blob is read.
              download(
                suggestedFilename('parquet'),
                new Uint8Array(bytes).slice(),
                'application/vnd.apache.parquet',
              );
              return;
            }
          } else {
            const data = await onExportData();
            if (data) {
              report('Writing file…');
              const body =
                format === 'csv' ? toCsv(data.columns, data.rows) : toJson(data.columns, data.rows);
              download(
                suggestedFilename(format),
                body,
                format === 'csv' ? 'text/csv;charset=utf-8' : 'application/json',
              );
              return;
            }
          }
          // Cache went away underneath us; fall through to the server.
        }

        const queryId = result.queryId;
        if (!queryId) throw new Error('Run the query again before exporting.');

        report('Exporting on the server…');
        const ticket = await requestExport(queryId, format);
        report(`Downloading ${formatBytes(ticket.sizeBytes)}…`);
        const blob = await fetchExport(ticket.downloadUrl);
        download(suggestedFilename(format), blob, blob.type || 'application/octet-stream');
      },
      [result.mode, result.queryId, onExportParquet, onExportData],
    );

    const running = result.status === 'running';
    const filterSummary = describeFilterSort(filterSort ?? EMPTY_FILTER_SORT);
    const hasActiveFilter =
      result.filters.length > 0 || (result.predicate ?? '').trim() !== '';
    const grouped = result.groupedColumns !== null && result.groupedColumns.length > 0;
    const groupSummary = describeGroup(group ?? EMPTY_GROUP);
    // A grouped result has different columns from the SQL above it.
    const displayColumns = grouped
      ? (result.groupedColumns as typeof result.columns)
      : result.columns;

    return (
      <section
        className="flex flex-col overflow-hidden rounded-sm border bg-surface transition-colors"
        style={{
          borderColor: active ? 'var(--c-line-strong)' : 'var(--c-line)',
          boxShadow: active ? 'inset 3px 0 0 var(--c-local)' : 'none',
          flex: expanded ? '1 1 auto' : '0 0 auto',
          minHeight: 0,
        }}
        aria-label={`Cell ${index + 1}`}
        onFocusCapture={onFocus}
        data-testid={`cell-${cellId}`}
        data-active={active}
      >
        {/* Toolbar */}
        <header className="flex h-8 items-center gap-1.5 border-b border-line px-2">
          <span className="eyebrow tabular-nums">
            {String(index + 1).padStart(2, '0')}
          </span>

          <button
            type="button"
            className="btn btn-run"
            onClick={running ? onCancel : onRun}
            title={running ? 'Cancel this query' : shortcut('Run cell — ⌘↵')}
          >
            {running ? 'Cancel' : '▶ Run'}
          </button>

          {running ? <Spinner label="" /> : null}

          <button
            type="button"
            className="btn"
            aria-pressed={filterOpen}
            aria-label="Filter and sort"
            disabled={result.status !== 'success'}
            onClick={() => setFilterOpen((open) => !open)}
            title="Filter &amp; Sort"
          >
            ⑆ Filter &amp; Sort
            {filterSummary ? (
              <span className="text-[10px] text-local">{filterSummary}</span>
            ) : null}
          </button>

          <button
            type="button"
            className="btn"
            aria-pressed={groupOpen}
            aria-label="Group and aggregate"
            disabled={result.status !== 'success'}
            onClick={() => setGroupOpen((open) => !open)}
            title="Group &amp; Aggregate"
          >
            ⊞ Group
            {isGroupActive(group ?? EMPTY_GROUP) ? (
              <span className="text-[10px] text-local">on</span>
            ) : null}
          </button>

          <div className="ml-auto flex items-center gap-0.5">
            <button type="button" className="icon-btn" onClick={formatCurrent} title="Format SQL">
              ⌁
            </button>
            <button
              type="button"
              className="icon-btn"
              onClick={() => onMove(-1)}
              disabled={index === 0}
              title={shortcut('Move up — ⌘↑')}
              aria-label="Move cell up"
            >
              ↑
            </button>
            <button
              type="button"
              className="icon-btn"
              onClick={() => onMove(1)}
              disabled={index === total - 1}
              title={shortcut('Move down — ⌘↓')}
              aria-label="Move cell down"
            >
              ↓
            </button>
            <button
              type="button"
              className="icon-btn"
              onClick={onDuplicate}
              title="Duplicate cell"
              aria-label="Duplicate cell"
            >
              ⧉
            </button>
            <button
              type="button"
              className="icon-btn"
              onClick={onAddBelow}
              title="Add cell below"
              aria-label="Add cell below"
            >
              +
            </button>
            <button
              type="button"
              className="icon-btn hover:!text-danger"
              onClick={onDelete}
              title="Delete cell"
              aria-label="Delete cell"
            >
              ✕
            </button>

            <ExportMenu
              disabled={result.status !== 'success'}
              mode={result.mode}
              rowCount={result.totalRows}
              onExport={runExport}
            />
          </div>
        </header>

        {formatError ? (
          <p className="border-b border-line bg-remote-dim px-2.5 py-1 text-[10px] text-remote" role="status">
            {formatError}
          </p>
        ) : null}

        {/* Editor */}
        <div
          className="overflow-auto border-b border-line"
          style={{ maxHeight: expanded ? '40%' : 260, minHeight: 76 }}
        >
          <SqlEditor
            ref={editorRef}
            value={sql}
            ariaLabel={`SQL for cell ${index + 1}`}
            placeholder="SELECT … "
            error={result.status === 'error' ? result.error : null}
            onChange={onSqlChange}
            onRun={onRun}
            onRunSelection={() => onRunSelection(editorRef.current?.getSelection() ?? '')}
            onRunAndAdvance={onRunAndAdvance}
            getCatalog={getCatalog}
          />
        </div>

        {filterOpen && result.status === 'success' ? (
          <FilterSortPanel
            columns={result.columns}
            state={filterSort ?? EMPTY_FILTER_SORT}
            mode={result.mode}
            onChange={onFilterSortChange}
            onApply={onApplyFilterSort}
            onClose={() => setFilterOpen(false)}
            loadDistinct={onLoadDistinct}
          />
        ) : null}

        {groupOpen && result.status === 'success' ? (
          <GroupPanel
            columns={result.columns}
            state={group ?? EMPTY_GROUP}
            mode={result.mode}
            onChange={onGroupChange}
            onApply={onApplyGroup}
            onClose={() => setGroupOpen(false)}
          />
        ) : null}

        {/* Result */}
        {result.status !== 'idle' ? (
          <div className="flex min-h-0 flex-col" style={{ flex: expanded ? '1 1 auto' : '0 0 auto' }}>
            {grouped ? (
              <div
                className="flex items-center gap-2 border-b border-line bg-local-dim px-2.5 py-1"
                data-testid="grouped-banner"
                role="status"
              >
                <span className="eyebrow text-local">Grouped</span>
                <span className="text-[10px] text-muted">
                  {groupSummary} — these columns are aggregates, not the columns of the SQL above.
                </span>
                <button
                  type="button"
                  className="btn ml-auto"
                  onClick={() => {
                    onGroupChange(EMPTY_GROUP);
                    onApplyGroup(null);
                  }}
                >
                  Ungroup
                </button>
              </div>
            ) : null}

            <ModeStrip
              mode={result.mode}
              reason={result.modeReason}
              cachedRows={result.mode === 'local' ? result.totalRows : 0}
              totalRows={result.totalRows}
              lastViewMs={result.lastViewMs}
            />

            {result.status === 'error' && result.error ? (
              <ErrorState error={result.error} onRetry={onRun} />
            ) : result.status === 'running' && result.rows.length === 0 ? (
              <div className="flex h-24 items-center justify-center">
                <Spinner label="Waiting for the first batch" />
              </div>
            ) : result.status === 'success' && result.rows.length === 0 ? (
              <div className="h-24">
                <EmptyState
                  title={hasActiveFilter ? 'No rows match this filter' : '0 rows'}
                  hint={
                    hasActiveFilter
                      ? 'The query returned rows, but none satisfy the current filter.'
                      : 'The query succeeded and returned no rows.'
                  }
                  action={
                    hasActiveFilter ? (
                      <button
                        type="button"
                        className="btn btn-run"
                        onClick={() => {
                          onFilterSortChange(EMPTY_FILTER_SORT);
                          onApplyFilterSort({ filters: [], sort: [], predicate: null });
                        }}
                      >
                        Clear filter
                      </button>
                    ) : undefined
                  }
                />
              </div>
            ) : (
              <div
                className="flex min-h-0 min-w-0"
                style={{ height: expanded ? undefined : 340, flex: expanded ? '1 1 auto' : undefined }}
              >
                <ResultGrid
                  columns={displayColumns}
                  rows={result.rows}
                  totalRows={result.totalRows}
                  sort={result.sort}
                  filters={result.filters}
                  columnOrder={result.columnOrder}
                  hiddenColumns={result.hiddenColumns}
                  onSortChange={onSortChange}
                  onFiltersChange={onFiltersChange}
                  onColumnOrderChange={onColumnOrderChange}
                  onToggleColumn={onToggleColumn}
                  columnWidths={result.columnWidths}
                  onColumnWidthsChange={onColumnWidthsChange}
                  onFocusColumn={onFocusColumn}
                  onFilterInPanel={(name) => {
                    const current = filterSort ?? EMPTY_FILTER_SORT;
                    const column = result.columns.find((entry) => entry.name === name);
                    onFilterSortChange({
                      ...current,
                      mode: 'builder',
                      rows: [...current.rows, newFilterRow(name, column?.type)],
                    });
                    setFilterOpen(true);
                  }}
                />
              </div>
            )}

            <CellStatusBar result={result} />
          </div>
        ) : null}
      </section>
    );
  },
);
